import { useWizardStore } from '@/store/wizardStore'
import type { AppearanceStyle } from '@/store/wizardStore'
import type { Lang } from '@/hooks/useLang'

interface Step6AppearanceProps { lang: Lang }

const STYLES: {
  id: AppearanceStyle
  icon: string
  fr: { title: string; desc: string }
  en: { title: string; desc: string }
}[] = [
  {
    id: 'humanoid',
    icon: 'person',
    fr: { title: 'Humanoïde', desc: 'Formes anthropomorphes — tête, torse, membres. Favorise l\'acceptation sociale.' },
    en: { title: 'Humanoid', desc: 'Anthropomorphic form — head, torso, limbs. Promotes social acceptance.' },
  },
  {
    id: 'industrial',
    icon: 'precision_manufacturing',
    fr: { title: 'Industriel', desc: 'Design fonctionnel optimisé pour la robustesse et la performance brute.' },
    en: { title: 'Industrial', desc: 'Functional design optimized for robustness and raw performance.' },
  },
  {
    id: 'hybrid',
    icon: 'auto_awesome_mosaic',
    fr: { title: 'Hybride', desc: 'Équilibre entre ergonomie humaine et efficacité mécanique.' },
    en: { title: 'Hybrid', desc: 'Balance between human ergonomics and mechanical efficiency.' },
  },
  {
    id: 'custom',
    icon: 'design_services',
    fr: { title: 'Sur mesure', desc: 'Conception entièrement personnalisée selon vos contraintes d\'image de marque.' },
    en: { title: 'Custom', desc: 'Fully customized design according to your brand image constraints.' },
  },
]

const ACCENT_COLORS = ['#00dbe9', '#ebb2ff', '#dbfcff', '#b600f8', '#ffffff', '#f8f4ff']

export default function Step6Appearance({ lang }: Step6AppearanceProps) {
  const { draft, update } = useWizardStore()
  const selected = draft.step6_appearance
  const accent = draft.step6_color_accent ?? '#00dbe9'

  const colorLabel = lang === 'fr' ? 'Couleur accent' : 'Accent color'

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {STYLES.map((s) => {
          const isSelected = selected === s.id
          return (
            <div
              key={s.id}
              onClick={() => update({ step6_appearance: s.id })}
              className={`glass-card p-8 rounded-xl border border-outline-variant/20 flex flex-col items-center text-center gap-4 cursor-pointer ${isSelected ? 'ring-1 ring-primary-container bg-surface-container-highest/80' : ''}`}
            >
              <div className={`w-16 h-16 rounded-xl flex items-center justify-center transition-colors ${isSelected ? 'bg-primary-container/20 text-primary-container' : 'bg-surface-container-highest text-primary'}`}>
                <span className="material-symbols-outlined text-4xl">{s.icon}</span>
              </div>
              <h4 className={`font-headline text-lg font-bold transition-colors ${isSelected ? 'text-primary-container' : 'group-hover:text-primary'}`}>
                {s[lang].title}
              </h4>
              <p className="text-xs text-on-surface-variant leading-relaxed">{s[lang].desc}</p>
            </div>
          )
        })}
      </div>

      {/* Accent color picker */}
      <div className="glass-card p-8 rounded-xl border border-outline-variant/20 max-w-md">
        <p className="font-headline text-base font-bold text-on-surface mb-6">{colorLabel}</p>
        <div className="flex items-center gap-4 flex-wrap">
          {ACCENT_COLORS.map((color) => (
            <button
              key={color}
              onClick={() => update({ step6_color_accent: color })}
              className="w-9 h-9 rounded-full border-2 transition-all active:scale-90"
              style={{
                backgroundColor: color,
                borderColor: accent === color ? color : 'transparent',
                boxShadow: accent === color ? `0 0 12px ${color}60` : 'none',
                outline: accent === color ? `2px solid ${color}40` : 'none',
                outlineOffset: '3px',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
